<template>
    <header class="pb-20 bg-halo-cobalt lg:pb-0">
        <div class="relative max-w-5xl px-4 mx-auto sm:px-6 lg:max-w-screen-2xl lg:px-8">
            <div class="relative flex flex-wrap items-center justify-center lg:justify-between">
                <!-- Logo -->
                <div class="pt-10 mt-8 lg:mt-0 lg:pt-0 absolute left-0 shrink-0 lg:static">
                    <RouterLink
                        class="flex items-center justify-between space-x-2 h-12 sm:h-16"
                        :to="{ name: 'home' }"
                        data-cy="app-logo"
                    >
                        <template v-if="!organization || (!organization?.displayName && !organization?.logoUrl)">
                            <HaloLogo class="mt-2"></HaloLogo>
                        </template>
                        <template v-else>
                            <div class="flex items-center gap-4">
                                <img
                                    v-if="organization?.logoUrl"
                                    class="max-h-16"
                                    :src="organization.logoUrl"
                                    :alt="organization.displayName"
                                >
                                <span
                                    class="text-xl text-white font-bold"
                                    :class="{'hidden sm:flex': organization?.logoUrl}"
                                >
                                    {{ organization.displayName }}
                                </span>
                            </div>
                        </template>
                    </RouterLink>
                </div>

                <div class="hidden lg:ml-4 lg:flex lg:items-center lg:py-5 lg:pr-0.5 space-x-3">
                    <Menu>
                        <div class="relative shrink-0 ml-4">
                            <MenuButton
                                id="user-menu-button"
                                type="button"
                                class="flex text-sm text-white rounded-full ring-2 ring-white ring-opacity-20 focus:outline-none
                                        focus:ring-opacity-100"
                                data-cy="user-menu-button"
                                aria-expanded="false"
                                aria-haspopup="true"
                            >
                                <span class="sr-only">{{ $t('navigation.openUserMenu') }}</span>
                                <div
                                    v-if="user.person?.imageUrl"
                                    class="bg-cover bg-no-repeat bg-center size-8 rounded-full"
                                    :style="{ backgroundImage: `url(${user.person.imageUrl})` }"
                                ></div>
                                <UserCircleIcon v-else class="w-8 h-8"></UserCircleIcon>
                            </MenuButton>

                            <transition
                                leave-active-class="transition duration-75 ease-in"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0"
                            >
                                <MenuItems
                                    class="absolute z-40 w-48 py-1 mt-2 origin-top-right bg-white rounded-md shadow-lg -right-2 ring-1
                                            ring-black ring-opacity-5 focus:outline-none"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="user-menu-button"
                                    tabindex="-1"
                                >
                                    <MenuItem>
                                        <p
                                            class="block break-words px-4 py-2 text-sm text-gray-700"
                                            aria-current="page"
                                        >
                                            {{ $t('navigation.signedInAs') }}
                                            <span class="font-medium">{{ user.email }}</span>
                                        </p>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <a
                                            class="block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                                            :class="{ 'bg-gray-100': active }"
                                            data-cy="your-profile-button"
                                            aria-current="page"
                                            @click="$router.push({ name: 'profile.index' })"
                                        >
                                            {{ $t('navigation.yourProfile') }}
                                        </a>
                                    </MenuItem>
                                    <MenuItem>
                                        <LogoutButton
                                            class="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100
                                                    hover:text-gray-800"
                                            role="menuitem"
                                            tabindex="-1"
                                        ></LogoutButton>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </div>
                    </Menu>
                </div>

                <Popover
                    v-slot="{ open }"
                    as="div"
                    class="absolute right-0 shrink-0 lg:hidden pt-10 lg:pt-0 z-20"
                >
                    <PopoverButton>
                        <button
                            data-tour="mobileMenuButton"
                            type="button"
                            class="inline-flex items-center justify-center p-2 mt-8 bg-transparent rounded-md text-cyan-200
                                    hover:text-white hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white"
                            aria-expanded="false"
                        >
                            <span class="sr-only">{{ $t('navigation.openMainMenu') }}</span>
                            <MenuIcon
                                class="w-6 h-6"
                                :class="{ 'hidden': open, 'block': !open }"
                                stroke="currentColor"
                            ></MenuIcon>
                            <XIcon
                                class="w-6 h-6"
                                :class="{ 'block': open, 'hidden': !open }"
                                stroke="currentColor"
                            >
                            </XIcon>
                        </button>
                    </PopoverButton>

                    <transition
                        enter-active-class="duration-150 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="duration-150 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <PopoverOverlay
                            class="z-20 bg-black"
                            :class="{ 'bg-opacity-25 fixed inset-0': open, 'opacity-0': !open }"
                        ></PopoverOverlay>
                    </transition>

                    <transition
                        enter-active-class="ease-out duration-250"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="ease-in duration-250"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <PopoverPanel v-slot="{close}">
                            <div class="lg:hidden">
                                <div
                                    class="fixed inset-x-0 top-0 z-30 w-full max-w-5xl p-2 mx-auto transition origin-top transform"
                                >
                                    <div
                                        class="bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                                    >
                                        <div class="pt-3 pb-2">
                                            <div class="flex justify-end mx-4 -mb-2">
                                                <button
                                                    type="button"
                                                    class="inline-flex items-center justify-center p-2 text-gray-400 bg-white rounded-md
                                                            hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2
                                                            focus:ring-inset focus:ring-cyan-500"
                                                    @click="close()"
                                                >
                                                    <span class="sr-only">Close menu</span>
                                                    <XIcon class="justify-end w-6 h-6 stroke-current"></XIcon>
                                                </button>
                                            </div>
                                            <div class="px-2 mt-3 space-y-1">
                                                <div
                                                    class="block w-full border-b border-white border-opacity-20 lg:hidden "
                                                >
                                                    <div
                                                        class="grid grid-cols-1 gap-y-2"
                                                        @click="close()"
                                                    >
                                                        <DonorLinks></DonorLinks>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="pt-0 pb-4 sm:pt-4 sm:pb-2">
                                            <div class="w-full mt-2 border-t border-gray-200"></div>

                                            <div class="flex items-center px-2 mt-5">
                                                <div
                                                    v-if="user.person?.imageUrl"
                                                    class="bg-cover bg-no-repeat bg-center size-8 rounded-full ml-2"
                                                    :style="{ backgroundImage: `url(${user.person.imageUrl})` }"
                                                ></div>

                                                <div class="flex-1 min-w-0 ml-3">
                                                    <div class="text-base font-medium text-gray-800 truncate">
                                                        {{ getUserName(user) }}
                                                    </div>
                                                    <div class="text-sm font-medium text-gray-500 truncate">
                                                        {{ user.person?.contact?.email }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="px-2 mt-3 space-y-1">
                                                <RouterLink
                                                    :to="{ name: 'profile.index' }"
                                                    exact-active-class="bg-gray-100"
                                                    class="block px-3 py-2 text-base font-medium text-gray-900 rounded-md hover:bg-gray-100 hover:text-gray-800"
                                                    aria-current="page"
                                                    @click.prevent="close()"
                                                >
                                                    {{ $t('navigation.yourProfile') }}
                                                </RouterLink>

                                                <LogoutButton
                                                    class="block px-3 py-2 text-base font-medium text-gray-900 rounded-md hover:bg-gray-100 hover:text-gray-800"
                                                ></LogoutButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </PopoverPanel>
                    </transition>
                </Popover>
            </div>
        </div>
    </header>
</template>

<script setup lang="ts">
import HaloLogo from '@/components/logos/HaloLogo.vue';
import { getUserName } from '@/utils/helper';
import LogoutButton from '@/components/buttons/LogoutButton.vue';
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Popover,
    PopoverButton,
    PopoverOverlay,
    PopoverPanel
} from '@headlessui/vue';
import { MenuIcon, XIcon } from '@heroicons/vue/outline';
import { UserCircleIcon } from '@heroicons/vue/solid';
import { RouterLink } from 'vue-router';
import DonorLinks from '@/components/navigation/DonorLinks.vue';
import { userState } from '@/composables/useUser';
import { organizationState } from '@/composables/useOrganization';

const user = userState.getUser;
const { organization } = organizationState;
</script>
